import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

const POSTER = "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/l9m6m6sa_affiche%20Octobre%20Rose.webp";
const PHOTOS = [
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/1w5658ri_Moi%2C%20La%20maire%20et%20l%27adjointe%20%C3%A0%20la%20culture%20de%20Castelnau.webp",
    caption: { fr: "Vernissage · avec la maire et l'adjointe à la culture de Castelnau d'Estrétefonds", en: "Opening night · with the mayor and deputy for culture of Castelnau d'Estrétefonds" },
    span: "md:col-span-4 aspect-[4/3]",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/ck07l1zz_Moi%20presentant%20l%27expo.webp",
    caption: { fr: "Présentation de l'exposition · maison éclusière", en: "Presenting the exhibition · lock-keeper's house" },
    span: "md:col-span-4 aspect-[4/3]",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/c82rwe4t_Gift.webp",
    caption: { fr: "Œuvre de Gift Osague · exposée sur les platanes du canal", en: "Work by Gift Osague · displayed on the canal's plane trees" },
    span: "md:col-span-4 aspect-[4/3]",
  },
];

export default function Editions2025() {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <section className="relative py-32 md:py-40 px-6 md:px-12 bg-bg" data-testid="editions-2025">
      <div className="max-w-[1600px] mx-auto">
        <div className="grid md:grid-cols-12 gap-12 mb-16 md:mb-20">
          <div className="md:col-span-6">
            <div className="meta-label text-cinabre">§ {en ? "Previous edition" : "Édition précédente"} · Canal'Art 2025 · Octobre Rose</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>June 14<br/>→ October 31 <em className="italic">2025</em>.</> : <>14 juin<br/>→ 31 octobre <em className="italic">2025</em>.</>}
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? (
                <>The 2025 edition explored <span className="text-cinabre">Octobre Rose</span> through photography, painting, music and performance — where the fight against breast cancer meets art along the canal. A dress rehearsal, in Castelnau d'Estrétefonds, before Canal'Art 2026.</>
              ) : (
                <>L'édition 2025 déclinait <span className="text-cinabre">Octobre Rose</span> en photo, peinture, musique et performances — quand la mobilisation contre le cancer du sein rencontre l'art en bord de canal. Une répétition générale, à Castelnau d'Estrétefonds, avant Canal'Art 2026.</>
              )}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8">
          {/* Poster */}
          <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-4 group"
            data-testid="editions-2025-poster"
          >
            <div className="border border-fg/15 overflow-hidden bg-fg/5 aspect-[2/3]">
              <img
                src={POSTER}
                alt="Affiche Canal'Art 2025 · Octobre Rose · Castelnau d'Estrétefonds"
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              {en ? "Official poster · Canal'Art 2025" : "Affiche officielle · Canal'Art 2025"}
            </figcaption>
          </motion.figure>

          {/* Photo mosaic */}
          <div className="md:col-span-8 grid md:grid-cols-12 gap-6 md:gap-8 auto-rows-min">
            <motion.figure
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="md:col-span-12 group"
              data-testid="editions-2025-photo-0"
            >
              <div className="border border-fg/15 overflow-hidden bg-fg/5 aspect-[16/10]">
                <img src={PHOTOS[0].url} alt={PHOTOS[0].caption[lang]} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" loading="lazy" />
              </div>
              <figcaption className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{PHOTOS[0].caption[lang]}</figcaption>
            </motion.figure>
            <motion.figure
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="md:col-span-7 group"
              data-testid="editions-2025-photo-1"
            >
              <div className="border border-fg/15 overflow-hidden bg-fg/5 aspect-[4/3]">
                <img src={PHOTOS[1].url} alt={PHOTOS[1].caption[lang]} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" loading="lazy" />
              </div>
              <figcaption className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{PHOTOS[1].caption[lang]}</figcaption>
            </motion.figure>
            <motion.figure
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="md:col-span-5 group"
              data-testid="editions-2025-photo-2"
            >
              <div className="border border-fg/15 overflow-hidden bg-fg/5 aspect-[3/4]">
                <img src={PHOTOS[2].url} alt={PHOTOS[2].caption[lang]} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" loading="lazy" />
              </div>
              <figcaption className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{PHOTOS[2].caption[lang]}</figcaption>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  );
}
